import { getSupabaseClient } from "../../providers/supabase/supabase";
import { logActivity } from "../myday/logActivity";
import type { Offer } from "../useOffers";
import { promoteMany } from "./promote";

export type VisitStop = { id: number; company_id?: number | null };

/**
 * Oferta con la que se promueve una parada sin empresa, sin preguntar nada: la del filtro
 * activo del Directorio si existe, luego la de mayor prioridad entre las que encajan con
 * la empresa (`directory.fits`), y por último la de mayor prioridad. `offers` debe venir
 * ordenada por prioridad (como `useOffers`).
 */
export const pickVisitOffer = (
  offers: Offer[],
  fits: string[] | null | undefined,
  preferredSlug?: string | null,
): Offer | null =>
  offers.find((o) => o.slug === preferredSlug) ??
  offers.find((o) => fits?.includes(o.slug)) ??
  offers[0] ??
  null;

/** Oferta que sugiere el filtro del Directorio (`_ofertas` o `fits@cs`), si hay una sola. */
export const filterOfferSlug = (
  filter: Record<string, unknown> | undefined,
): string | null => {
  const list = filter?._ofertas;
  if (Array.isArray(list) && list.length === 1) return String(list[0]);
  const cs = String(filter?.["fits@cs"] ?? "").replace(/[{}]/g, "");
  return cs && !cs.includes(",") ? cs : null;
};

export type VisitTarget = {
  companyId: number;
  companyName: string;
  contactId: number;
  workspaceId: number;
  deal: { id: number; key: string | null; offer_id: number } | null;
};

/**
 * Deja lista la parada para registrarle una visita. Si no tiene `company_id` la promueve a
 * empresa con una oferta elegida sola (un toque, sin pedir datos). El `workspace_id` sale
 * de la propia empresa, nunca del default de la base.
 */
export const resolveVisitTarget = async (a: {
  stop: VisitStop;
  offers: Offer[];
  preferredSlug?: string | null;
  salesId: number;
}): Promise<VisitTarget> => {
  const sb = getSupabaseClient();
  let companyId = a.stop.company_id ?? null;
  let dealId: number | null = null;

  if (companyId == null) {
    const { data: dir, error } = await sb
      .from("directory")
      .select("company_id, fits")
      .eq("id", a.stop.id)
      .single();
    if (error) throw new Error(`No se pudo leer la empresa: ${error.message}`);
    companyId = dir.company_id;
    if (companyId == null) {
      const offer = pickVisitOffer(a.offers, dir.fits, a.preferredSlug);
      if (!offer)
        throw new Error("No hay ofertas activas para abrir la empresa.");
      const r = await promoteMany([a.stop.id], offer, null, a.salesId);
      if (r.errors.length) throw new Error(r.errors[0]);
      companyId = r.companyIds[0];
      dealId = r.dealIds[0];
    }
  }

  const { data: company, error: e1 } = await sb
    .from("companies")
    .select("name, workspace_id")
    .eq("id", companyId)
    .single();
  if (e1) throw new Error(`No se pudo leer la empresa: ${e1.message}`);
  const { data: contacts, error: e2 } = await sb
    .from("contacts")
    .select("id")
    .eq("company_id", companyId)
    .order("id")
    .limit(1);
  if (e2) throw new Error(`No se pudo leer el contacto: ${e2.message}`);
  if (!contacts?.length)
    throw new Error("Esta empresa no tiene contacto donde guardar la visita.");

  let dealQuery = sb
    .from("deals")
    .select("id, key, offer_id")
    .eq("company_id", companyId)
    .is("archived_at", null);
  if (dealId != null) dealQuery = dealQuery.eq("id", dealId);
  const { data: deals, error: e3 } = await dealQuery
    .order("id", { ascending: false })
    .limit(1);
  if (e3) throw new Error(`No se pudo leer la oportunidad: ${e3.message}`);

  return {
    companyId,
    companyName: company.name,
    contactId: contacts[0].id,
    workspaceId: company.workspace_id,
    deal: deals?.[0] ?? null,
  };
};

/** Guarda la visita: nota con canal 'visita' (mismos resultados de siempre), sin siguiente paso ni tarea que cerrar. */
export const saveVisit = (t: VisitTarget, outcome: string, note: string) =>
  logActivity({
    contactId: t.contactId,
    dealId: t.deal?.id ?? null,
    channel: "visita",
    outcome,
    note,
    nextDays: 0,
    nextTaskType: "visit",
    nextTaskText: "",
    close: "none",
    workspaceId: t.workspaceId,
  });
